import React, { useEffect } from 'react';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns';
import { useLazyQuery } from '@apollo/client';

import KEY_ACTIVITIES_COMPLETED_AND_TIME_SPENT_REPORT_QUERY from 'graph/reports/key-activities-completed-and-time-spent-report-query';
import Popover from 'components/@common/Popover';
import BarAndLineChartCard from '../../../charts/BarAndLineChartCard';
import useReportQueryParams from 'components/ReportsModule/hooks/use-report-query-params';
import { getQueryVariables } from '../../../utils/report-param-utils';
import {
  ReportIntervalUnit,
  KeyActivitiesCompletedAndTimeSpentIntervalData
} from 'types/schema-generated-types';

const formatUTC = (isoString: string, formatOptions = {}): string => {
  const dateFormatter = new Intl.DateTimeFormat('en-us', { timeZone: 'UTC', ...formatOptions });
  return dateFormatter.format(parseISO(isoString));
};

const INTERVAL_CATEGORY_MAP = {
  [ReportIntervalUnit.DAY]: (
    intervals: KeyActivitiesCompletedAndTimeSpentIntervalData[]
  ): string[] =>
    intervals.map(({ interval }: { interval: string }) =>
      formatUTC(interval, { day: '2-digit', month: '2-digit', year: 'numeric' })
    ),
  [ReportIntervalUnit.WEEK]: (
    intervals: KeyActivitiesCompletedAndTimeSpentIntervalData[]
  ): string[] =>
    intervals.map(({ interval }: { interval: string }) =>
      formatUTC(interval, { day: '2-digit', month: '2-digit', year: 'numeric' })
    ),
  [ReportIntervalUnit.MONTH]: (
    intervals: KeyActivitiesCompletedAndTimeSpentIntervalData[]
  ): string[] =>
    intervals.map(({ interval }: { interval: string }) =>
      formatUTC(interval, { month: '2-digit', year: 'numeric' })
    )
};

const getSeries = (intervals: KeyActivitiesCompletedAndTimeSpentIntervalData[]): any => {
  const { activitiesCompletedSeries, averageMinutesSpentSeries } = intervals.reduce(
    (acc, interval) => {
      acc.activitiesCompletedSeries.push(interval.activitiesCompletedCount);
      acc.averageMinutesSpentSeries.push(interval.averageMinutesSpent);
      return acc;
    },
    {
      activitiesCompletedSeries: [] as number[],
      averageMinutesSpentSeries: [] as number[]
    }
  );

  return [
    {
      name: 'Activities',
      type: 'column',
      data: activitiesCompletedSeries
    },
    {
      name: 'Minutes',
      type: 'line',
      data: averageMinutesSpentSeries
    }
  ];
};

const ActivitiesCompletedAndTimeSpentChart: React.FC = () => {
  const { t } = useTranslation(['Reports', 'ReportsHelpCommon', 'ReportsHelpCharts']);
  const [{ entityId, entityType, dateRange }] = useReportQueryParams();
  const [fetchReport, { loading, data }] = useLazyQuery(
    KEY_ACTIVITIES_COMPLETED_AND_TIME_SPENT_REPORT_QUERY
  );

  const {
    intervalUnit = ReportIntervalUnit.DAY,
    intervals = [],
    totalActivitiesCompletedCount,
    averageWeeklyMinutesSpent
  } = data?.keyActivitiesCompletedAndTimeSpentReport || {};

  const series = getSeries(intervals);
  const categories = INTERVAL_CATEGORY_MAP[intervalUnit as ReportIntervalUnit](intervals);

  useEffect(() => {
    fetchReport({ variables: getQueryVariables({ entityId, entityType, dateRange }) });
  }, [fetchReport, entityId, entityType, dateRange]);

  return (
    <BarAndLineChartCard
      title={t('Activities & Average Time Spent')}
      helpText={t('activitiesCompletedAndTimeSpentByDate', { ns: 'ReportsHelpCharts' })}
      loading={loading}
      series={series}
      categories={categories}
      renderTextInformation={(): React.ReactElement => (
        <Grid spacing={4} container wrap="nowrap" direction="row">
          <Grid item>
            <Popover description={t('activitiesCompletedByStudent', { ns: 'ReportsHelpCommon' })}>
              <Typography component="h4" variant="body2">
                {t('Activities Completed')}
              </Typography>
            </Popover>
            <Typography variant="h1" component="span">
              {totalActivitiesCompletedCount}
            </Typography>
          </Grid>
          <Grid item>
            <Popover description={t('averageWeeklyTimeSpentSplit', { ns: 'ReportsHelpCommon' })}>
              <Typography component="h4" variant="body2">
                {t('Average Weekly Time Spent')}
              </Typography>
            </Popover>
            <Typography variant="h1" component="span">
              {t(`{{averageWeeklyMinutes}} minutes`, {
                averageWeeklyMinutes: averageWeeklyMinutesSpent
              })}
            </Typography>
          </Grid>
        </Grid>
      )}
    />
  );
};

export default React.memo(ActivitiesCompletedAndTimeSpentChart);
